// Shared readiness-result read helpers — ONE implementation used by the
// legacy Overview breakdown tabs and the Readiness page (2026-10-08).
// Pure: no React, no fetching.

import type { ReadinessFactorID, ReadinessResult } from "../readiness-types";

export const READINESS_FACTOR_LABELS: Record<ReadinessFactorID, string> = {
  buying_power: "Buying power",
  down_payment: "Down payment",
  cash_execution: "Cash execution",
  credit: "Credit",
  dti: "DTI",
  timeline: "Timeline",
  stability: "Stability",
  work_history: "Work history",
  profile_completeness: "Profile",
};

export type FactorBreakdown = {
  confidenceText: string | null;
  id: ReadinessFactorID;
  label: string;
  max: number;
  percent: number;
  ratio: number;
  value: number;
  // applies=false on the wire means the input that drives this factor is
  // missing. When the headline is also capped (score === null), surfacing
  // these rows as "—" keeps the breakdown parity with the headline — it
  // matches "we don't know" instead of pretending the input scored 0.
  isUnknown: boolean;
};

/** Months parsed from a `time_unlocks[].unlock_at` string ("in 4 months").
 *  Null when the string carries no month count. */
export function parseHorizonMonths(unlockAt: string): number | null {
  const match = unlockAt.match(/(\d+)\s*months?/i);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

export function buildFactorBreakdown(
  readiness: ReadinessResult,
  includeUnknown: boolean,
): FactorBreakdown[] {
  return (
    Object.entries(readiness.factors) as [
      ReadinessFactorID,
      { value: number; max: number; applies: boolean },
    ][]
  )
    .filter(([, factor]) => factor.applies || includeUnknown)
    .map(([id, factor]) => {
      const ratio = factor.max > 0 ? factor.value / factor.max : 0;
      const confidence = readiness.confidence_map[id];
      return {
        confidenceText:
          typeof confidence === "number"
            ? `${Math.round(confidence * 100)}% confidence`
            : null,
        id,
        label: READINESS_FACTOR_LABELS[id],
        max: factor.max,
        percent: clamp(Math.round(ratio * 100), 0, 100),
        ratio,
        value: factor.value,
        isUnknown: !factor.applies,
      };
    })
    .sort((a, b) => {
      // Known rows lead so the active factors stay at the top of the grid.
      if (a.isUnknown !== b.isUnknown) return a.isUnknown ? 1 : -1;
      return a.ratio - b.ratio;
    });
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
